import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { ariaAttr, callAllHandlers, dataAttr, useBoolean } from '@mote/helpers'
import { preventBubblingEvent } from '@mote/helpers/browser'
import type { PropGetter } from '@mote/helpers/types'
import type { ColorSchemes } from '../../theme/core/colors'
import type {
  RadioButtonContentProps,
  RadioButtonRootProps,
  RadioButtonSize
} from './RadioButton.styles'
import { useRadioButtonContext } from './RadioButtonContextProvider'

interface RadioEventTarget {
  checked: boolean
  value: string | number | undefined
}

export interface RadioEvent {
  target: RadioEventTarget
  stopPropagation: () => void
  preventDefault: () => void
  nativeEvent: React.ChangeEvent<HTMLInputElement>
}

export interface UseRadioProps {
  id?: string
  name?: string
  checked?: boolean
  defaultChecked?: boolean
  size?: RadioButtonSize
  disabled?: boolean
  invalid?: boolean
  required?: boolean
  readOnly?: boolean
  value?: string | number
  colorScheme?: ColorSchemes
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onChange?: (event: RadioEvent) => void
}

export const useRadio = (props: UseRadioProps) => {
  const [isFocused, isFocusedActions] = useBoolean(false)
  const [isHovered, isHoveredActions] = useBoolean(false)
  const [isChecked, setIsChecked] = useState(!!props.defaultChecked)

  const group = useRadioButtonContext()
  const isDisabled = props.disabled || group.disabledAll
  const size = group.inGroup ? group.size : props.size
  const name = props.name ?? group.name

  useEffect(() => {
    if (!group.inGroup) return

    setIsChecked(group.value === props.value)
  }, [group.value, group.inGroup, props.value])

  useEffect(() => {
    if (props.checked === undefined) return
    setIsChecked(Boolean(props.checked))
  }, [props.checked])

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation()
      if (isDisabled || (group.inGroup && isChecked)) return
      const customEvent = {
        target: {
          checked: !isChecked,
          value: props.value
        },
        stopPropagation: event.stopPropagation,
        preventDefault: event.preventDefault,
        nativeEvent: event
      }

      setIsChecked(!isChecked)
      if (group.inGroup) group.updateState?.(props.value)
      props.onChange?.(customEvent)
    },
    [
      isChecked,
      isDisabled,
      group.inGroup,
      props.onChange,
      props.value,
      group.updateState
    ]
  )

  const getLabelProps: PropGetter = (props = {}, ref = null) => ({
    ...props,
    ref,
    onMouseDown: preventBubblingEvent,
    onTouchStart: preventBubblingEvent
  })

  const getInputProps: PropGetter = useCallback(
    (inputProps = {}, ref = null) => {
      return {
        ...inputProps,
        ref,
        name,
        id: props.id,
        as: 'input',
        type: 'radio',
        value: props.value,
        onChange: handleOnChange,
        onFocus: callAllHandlers(inputProps.onFocus, isFocusedActions.setTrue),
        onBlur: callAllHandlers(inputProps.onBlur, isFocusedActions.setFalse),
        checked: isChecked,
        disabled: isDisabled,
        readOnly: props.readOnly,
        required: props.required,
        'aria-invalid': ariaAttr(props.invalid),
        'aria-disabled': ariaAttr(isDisabled),
        'aria-required': ariaAttr(props.required),
        'data-readonly': dataAttr(props.readOnly)
      }
    },
    [
      name,
      isDisabled,
      props.id,
      props.name,
      props.value,
      handleOnChange,
      props.onBlur,
      props.onFocus,
      isChecked,
      props.readOnly,
      props.required,
      props.invalid
    ]
  )

  const getRootProps: PropGetter<{}, RadioButtonRootProps> = useCallback(
    (labelProps, ref = null) => ({
      ...labelProps,
      ref,
      size: size || 'xl',
      'data-disabled': dataAttr(isDisabled),
      'data-checked': dataAttr(isChecked),
      'data-invalid': dataAttr(props.invalid)
    }),
    [isDisabled, isChecked, props.invalid, size]
  )

  const getRadioProps: PropGetter<{}, RadioButtonContentProps> = useCallback(
    (radioProps = {}, ref = null) => ({
      ...radioProps,
      ref,
      'data-disabled': dataAttr(isDisabled),
      'data-invalid': dataAttr(props.invalid),
      'data-checked': dataAttr(isChecked),
      'data-focused': dataAttr(isFocused),
      'data-hovered': dataAttr(isHovered),
      'data-readonly': dataAttr(props.readOnly),
      colorScheme: props.colorScheme || 'primary',
      onMouseEnter: callAllHandlers(
        radioProps.onMouseEnter,
        isHoveredActions.setTrue
      ),
      onMouseLeave: callAllHandlers(
        radioProps.onMouseLeave,
        isHoveredActions.setFalse
      ),
      onMouseDown: callAllHandlers(radioProps.onMouseDown, event => {
        event.preventDefault()
      })
    }),
    [
      isHovered,
      isDisabled,
      props.invalid,
      isChecked,
      isFocused,
      props.readOnly,
      props.colorScheme
    ]
  )

  return {
    state: {
      isHovered,
      isFocused,
      isChecked,
      isDisabled,
      isReadOnly: props.readOnly,
      isRequired: props.required,
      isInvalid: props.invalid
    },
    getLabelProps,
    getInputProps,
    getRootProps,
    getRadioProps
  }
}
